/**
 * Mermaid renderer for fenced ```mermaid blocks in artifact markdown.
 *
 * Its own file because eslint's react-refresh rule requires a module to hold
 * either components or plain exports, not both — artifactMarkdown.tsx exports
 * rendering config, so the one component it used had to move out.
 */
import { useEffect, useRef } from 'react';
import mermaid from 'mermaid';

// Chart colors converted from oklch CSS variables to hex for mermaid compatibility
const MERMAID_CHART_COLORS = {
  light: ['#f54900', '#009689', '#104e64', '#ffb900', '#fe9a00'],
  dark: ['#1447e6', '#00bc7d', '#fe9a00', '#ad46ff', '#ff2056'],
};

// Mermaid code block renderer
export function MermaidDiagram({ code }: { code: string }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const id = `mermaid-${Math.random().toString(36).slice(2, 9)}`;

    const isDark = document.documentElement.classList.contains('dark');
    const colors = isDark ? MERMAID_CHART_COLORS.dark : MERMAID_CHART_COLORS.light;

    mermaid.initialize({
      startOnLoad: false,
      theme: 'base',
      securityLevel: 'loose',
      themeVariables: {
        fontFamily: '"Inter", sans-serif',
        fontSize: '12px',
        primaryColor: colors[0],
        secondaryColor: colors[1],
        tertiaryColor: colors[2],
        primaryTextColor: isDark ? '#e2e8f0' : '#0a0f1a',
        primaryBorderColor: isDark ? '#2d3748' : '#e2e8f0',
        lineColor: isDark ? '#2d3748' : '#e2e8f0',
        textColor: isDark ? '#e2e8f0' : '#0a0f1a',
        mainBkg: isDark ? '#1a202c' : '#f1f5f9',
        nodeBorder: isDark ? '#2d3748' : '#e2e8f0',
        // Pie chart
        pie1: colors[0],
        pie2: colors[1],
        pie3: colors[2],
        pie4: colors[3],
        pie5: colors[4],
        pieTitleTextColor: isDark ? '#e2e8f0' : '#0a0f1a',
        pieSectionTextColor: isDark ? '#0a0f1a' : '#ffffff',
        pieLegendTextColor: isDark ? '#e2e8f0' : '#0a0f1a',
        pieStrokeColor: isDark ? '#0a0f1a' : '#ffffff',
        // XY Chart (bar/line via xychart-beta)
        xyChart: {
          backgroundColor: 'transparent',
          titleColor: isDark ? '#e2e8f0' : '#0a0f1a',
          plotColorPalette: colors.join(', '),
          xAxisLabelColor: isDark ? '#94a3b8' : '#64748b',
          xAxisTitleColor: isDark ? '#e2e8f0' : '#0a0f1a',
          xAxisTickColor: isDark ? '#2d3748' : '#e2e8f0',
          xAxisLineColor: isDark ? '#2d3748' : '#e2e8f0',
          yAxisLabelColor: isDark ? '#94a3b8' : '#64748b',
          yAxisTitleColor: isDark ? '#e2e8f0' : '#0a0f1a',
          yAxisTickColor: isDark ? '#2d3748' : '#e2e8f0',
          yAxisLineColor: isDark ? '#2d3748' : '#e2e8f0',
        },
      },
    });

    mermaid.render(id, code).then(({ svg }) => {
      if (containerRef.current) {
        containerRef.current.innerHTML = svg;
      }
    }).catch((err) => {
      if (containerRef.current) {
        containerRef.current.innerHTML = `<pre class="text-destructive text-sm">Mermaid error: ${err.message}</pre>`;
      }
    });
  }, [code]);

  return <div ref={containerRef} className="my-4 flex justify-center" />;
}

// Markdown components with mermaid support
